import { useEffect, useState } from "react";
import axios from "axios";
import {
  Users,
  Hammer,
  Megaphone,
  Truck,
  Wrench,
  HeartHandshake,
  CircleHelp,
  Clock,
  Send,
  Check,
  Loader2,
  AlertCircle,
  ArrowRight,
  Mountain,
  TrendingUp,
} from "lucide-react";
import { WHATSAPP_LINK } from "../constants";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ROLES = [
  {
    id: "operativo_campo",
    label: "Operativo sul campo",
    desc: "Pulizia, piantumazione, lavoro fisico",
    icon: Hammer,
  },
  {
    id: "coordinatore",
    label: "Coordinatore",
    desc: "Organizzo squadre e attività",
    icon: Users,
  },
  {
    id: "comunicazione",
    label: "Comunicazione / Social",
    desc: "Foto, video, post, racconto le azioni",
    icon: Megaphone,
  },
  {
    id: "logistica",
    label: "Logistica / Trasporti",
    desc: "Mezzi, attrezzi, spostamenti",
    icon: Truck,
  },
  {
    id: "esperto_tecnico",
    label: "Esperto tecnico",
    desc: "Agronomo, veterinario, edile, sanitario",
    icon: Wrench,
  },
  {
    id: "supporto_persone",
    label: "Supporto alle persone",
    desc: "Anziani, famiglie, ascolto",
    icon: HeartHandshake,
  },
  {
    id: "altro",
    label: "Altro ruolo",
    desc: "Lo definiamo insieme",
    icon: CircleHelp,
  },
];

const ACTIVITIES = [
  { id: "pulizia_territorio", label: "Pulizia del territorio" },
  { id: "cura_animali", label: "Cura degli animali" },
  { id: "riforestazione", label: "Riforestazione e piantumazione" },
  { id: "eventi_comunita", label: "Eventi e incontri di comunità" },
  { id: "supporto_fragili", label: "Supporto persone fragili" },
  { id: "smaltimento_potature", label: "Smaltimento potature" },
  { id: "comunicazione_social", label: "Comunicazione e social" },
  { id: "raccolta_fondi", label: "Raccolta fondi" },
  { id: "prevenzione_incendi", label: "Prevenzione incendi" },
  { id: "altro", label: "Altro" },
];

const AVAILABILITIES = [
  {
    id: "occasionale",
    label: "Occasionale",
    desc: "Poche ore quando posso",
  },
  {
    id: "settimanale",
    label: "Settimanale",
    desc: "Qualche ora a settimana",
  },
  {
    id: "costante",
    label: "Costante",
    desc: "Più giorni, in modo regolare",
  },
  {
    id: "emergenze",
    label: "Solo emergenze",
    desc: "Mi attivo nei momenti critici",
  },
];

export const Volunteer = () => {
  const [form, setForm] = useState({
    name: "",
    phone: "",
    email: "",
    role: "",
    activities: [],
    availability: "",
    message: "",
  });
  const [status, setStatus] = useState("idle");
  const [error, setError] = useState("");
  const [volunteerCount, setVolunteerCount] = useState(null);

  const fetchCount = () => {
    axios
      .get(`${API}/volunteers/stats`)
      .then((res) => setVolunteerCount(res.data?.total ?? 0))
      .catch(() => setVolunteerCount(0));
  };

  useEffect(() => {
    fetchCount();
  }, []);

  const handleField = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const toggleActivity = (id) => {
    setForm((prev) => {
      const has = prev.activities.includes(id);
      return {
        ...prev,
        activities: has
          ? prev.activities.filter((a) => a !== id)
          : [...prev.activities, id],
      };
    });
  };

  const reset = () => {
    setForm({
      name: "",
      phone: "",
      email: "",
      role: "",
      activities: [],
      availability: "",
      message: "",
    });
    setStatus("idle");
    setError("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.role) {
      setError("Scegli il ruolo che vuoi avere.");
      setStatus("error");
      return;
    }
    if (!form.availability) {
      setError("Indica quanto tempo puoi dedicare.");
      setStatus("error");
      return;
    }
    setStatus("loading");
    setError("");
    try {
      await axios.post(`${API}/volunteers`, {
        name: form.name,
        phone: form.phone,
        email: form.email || null,
        role: form.role,
        activities: form.activities,
        availability: form.availability,
        message: form.message || null,
      });
      setStatus("success");
      fetchCount();
    } catch (err) {
      setStatus("error");
      setError(
        err?.response?.data?.detail ||
          "Qualcosa è andato storto. Riprova o scrivici su WhatsApp."
      );
    }
  };

  return (
    <section
      id="volontari"
      data-testid="volunteer-section"
      className="relative bg-[#FBF9F6] py-24 sm:py-32 overflow-hidden"
    >
      {/* Decorative texture */}
      <div
        className="pointer-events-none absolute inset-0 opacity-[0.5]"
        style={{
          backgroundImage:
            "radial-gradient(circle at 15% 0%, #2E4F3B12 0%, transparent 40%), radial-gradient(circle at 85% 100%, #C15C3D10 0%, transparent 50%)",
        }}
      />

      <div className="relative max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
        {/* Live counter banner */}
        <div
          data-testid="volunteer-counter-banner"
          className="mb-10 sm:mb-14 overflow-hidden rounded-3xl bg-[#1A1A1A] text-white relative"
        >
          <div className="absolute -top-20 -right-20 w-80 h-80 rounded-full bg-[#25D366]/15 blur-3xl pointer-events-none" />
          <div className="relative px-6 sm:px-10 py-7 sm:py-8 flex flex-col sm:flex-row sm:items-center gap-5 sm:gap-8">
            <div className="flex items-center gap-4">
              <span className="relative inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-[#25D366]/15 border border-[#25D366]/30">
                <TrendingUp className="w-6 h-6 text-[#25D366]" />
                <span className="absolute -top-1 -right-1 flex h-3 w-3">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#25D366] opacity-75" />
                  <span className="relative inline-flex rounded-full h-3 w-3 bg-[#25D366]" />
                </span>
              </span>
              <div className="leading-none">
                <div
                  data-testid="volunteer-counter-number"
                  className="font-display text-5xl sm:text-6xl font-black text-[#E8C9A0] tracking-tight"
                >
                  {volunteerCount === null ? "—" : volunteerCount}
                </div>
                <div className="mt-2 text-[11px] font-bold uppercase tracking-[0.22em] text-white/70">
                  {volunteerCount === 1
                    ? "persona si è candidata come volontario"
                    : "persone si sono candidate come volontari"}
                </div>
              </div>
            </div>
            <div className="hidden sm:block h-14 w-px bg-white/15" />
            <p className="text-sm sm:text-base text-white/75 leading-relaxed flex-1 max-w-xl">
              Ogni candidatura conta. Ogni mano è un pezzo della ripartenza.
              <span className="block mt-1 text-white font-semibold">
                Aggiungi la tua ↓
              </span>
            </p>
          </div>
        </div>

        {/* Header */}
        <div className="max-w-3xl mb-14 sm:mb-20">
          <span className="inline-flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.22em] text-[#C15C3D]">
            <Mountain className="w-3.5 h-3.5" />
            Diventa volontario
          </span>
          <h2
            data-testid="volunteer-title"
            className="mt-5 font-display text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight text-[#1A1A1A] leading-[0.95]"
          >
            Il nostro monte
            <br />
            <span className="italic font-light text-[#2E4F3B]">
              ha bisogno di te.
            </span>
          </h2>
          <p className="mt-7 text-lg sm:text-xl text-[#5C5C5C] max-w-2xl leading-relaxed">
            Dopo la tragedia che abbiamo subito, dobbiamo aiutarci a vicenda.
            Fare di tutto per il nostro monte e per la nostra comunità. Scegli{" "}
            <span className="text-[#1A1A1A] font-semibold">il tuo ruolo</span>,{" "}
            <span className="text-[#1A1A1A] font-semibold">le attività</span>{" "}
            che senti tue e{" "}
            <span className="text-[#1A1A1A] font-semibold">quanto tempo</span>{" "}
            puoi dedicare. Ogni mano conta.
          </p>
        </div>

        {status === "success" ? (
          <div
            data-testid="volunteer-success"
            className="rounded-3xl bg-[#2E4F3B] text-white p-10 sm:p-16 text-center max-w-3xl mx-auto"
          >
            <div className="w-16 h-16 rounded-full bg-white/15 flex items-center justify-center mx-auto mb-6">
              <Check className="w-8 h-8 text-[#E8C9A0]" />
            </div>
            <h3 className="font-display text-3xl sm:text-4xl font-black leading-tight">
              Benvenuto in famiglia.
            </h3>
            <p className="mt-4 text-white/85 max-w-xl mx-auto leading-relaxed">
              Abbiamo ricevuto la tua candidatura. Verrai{" "}
              <span className="text-[#E8C9A0] font-semibold">
                aggiunto automaticamente
              </span>{" "}
              al gruppo (bacheca) dei volontari al numero che hai indicato.
            </p>
            <p className="mt-3 text-sm text-white/65 max-w-xl mx-auto leading-relaxed">
              Da lì coordineremo i prossimi passi insieme.
            </p>
            <div className="mt-8 flex justify-center">
              <button
                type="button"
                onClick={reset}
                data-testid="volunteer-new-btn"
                className="inline-flex items-center justify-center gap-2 rounded-full bg-white/10 text-white font-semibold px-8 py-4 hover:bg-white/20 border border-white/20 transition-all"
              >
                Candida un'altra persona
              </button>
            </div>
          </div>
        ) : (
          <form
            onSubmit={handleSubmit}
            data-testid="volunteer-form"
            className="space-y-12"
          >
            {/* Step 1 — Role */}
            <fieldset>
              <legend className="text-[10px] font-bold uppercase tracking-[0.22em] text-[#C15C3D] mb-5">
                1. Che ruolo vuoi avere?
              </legend>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {ROLES.map((r) => {
                  const Icon = r.icon;
                  const active = form.role === r.id;
                  return (
                    <button
                      type="button"
                      key={r.id}
                      onClick={() => {
                        setForm({ ...form, role: r.id });
                        if (status === "error") setStatus("idle");
                      }}
                      data-testid={`volunteer-role-${r.id}`}
                      className={`text-left rounded-2xl p-5 border transition-all duration-300 group ${
                        active
                          ? "bg-[#1A1A1A] text-white border-[#1A1A1A] shadow-lg"
                          : "bg-white border-[#E5E1D8] hover:border-[#2E4F3B]"
                      }`}
                    >
                      <div className="flex items-start gap-4">
                        <span
                          className={`w-11 h-11 rounded-xl flex items-center justify-center shrink-0 ${
                            active
                              ? "bg-[#25D366] text-white"
                              : "bg-[#FBF9F6] text-[#2E4F3B] border border-[#E5E1D8]"
                          }`}
                        >
                          <Icon className="w-5 h-5" strokeWidth={2} />
                        </span>
                        <div className="flex-1 min-w-0">
                          <div
                            className={`font-display font-bold text-base sm:text-lg ${
                              active ? "text-white" : "text-[#1A1A1A]"
                            }`}
                          >
                            {r.label}
                          </div>
                          <div
                            className={`text-xs mt-1 leading-relaxed ${
                              active ? "text-white/65" : "text-[#5C5C5C]"
                            }`}
                          >
                            {r.desc}
                          </div>
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            </fieldset>

            {/* Step 2 — Activities (multi-select) */}
            <fieldset>
              <legend className="text-[10px] font-bold uppercase tracking-[0.22em] text-[#C15C3D] mb-5">
                2. A quali attività vuoi dedicarti?{" "}
                <span className="text-[#5C5C5C] normal-case tracking-normal text-[11px] font-medium ml-2">
                  (anche più di una)
                </span>
              </legend>
              <div className="flex flex-wrap gap-2.5">
                {ACTIVITIES.map((a) => {
                  const active = form.activities.includes(a.id);
                  return (
                    <button
                      type="button"
                      key={a.id}
                      onClick={() => toggleActivity(a.id)}
                      data-testid={`volunteer-activity-${a.id}`}
                      className={`inline-flex items-center gap-2 rounded-full px-4 py-2.5 border text-sm font-medium transition-all ${
                        active
                          ? "bg-[#2E4F3B] text-white border-[#2E4F3B]"
                          : "bg-white text-[#1A1A1A] border-[#E5E1D8] hover:border-[#2E4F3B]"
                      }`}
                    >
                      <span
                        className={`inline-flex items-center justify-center w-4 h-4 rounded-full border ${
                          active
                            ? "bg-white border-white"
                            : "border-[#E5E1D8]"
                        }`}
                      >
                        {active && (
                          <Check className="w-3 h-3 text-[#2E4F3B]" strokeWidth={3} />
                        )}
                      </span>
                      {a.label}
                    </button>
                  );
                })}
              </div>
            </fieldset>

            {/* Step 3 — Availability */}
            <fieldset>
              <legend className="text-[10px] font-bold uppercase tracking-[0.22em] text-[#C15C3D] mb-5">
                3. Quanto tempo puoi dedicare?
              </legend>
              <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
                {AVAILABILITIES.map((av) => {
                  const active = form.availability === av.id;
                  return (
                    <button
                      type="button"
                      key={av.id}
                      onClick={() => {
                        setForm({ ...form, availability: av.id });
                        if (status === "error") setStatus("idle");
                      }}
                      data-testid={`volunteer-availability-${av.id}`}
                      className={`text-left rounded-2xl p-5 border transition-all ${
                        active
                          ? "bg-[#C15C3D] text-white border-[#C15C3D] shadow-md"
                          : "bg-white border-[#E5E1D8] hover:border-[#C15C3D]"
                      }`}
                    >
                      <div className="flex items-center gap-2">
                        <Clock
                          className={`w-4 h-4 ${
                            active ? "text-white" : "text-[#C15C3D]"
                          }`}
                        />
                        <span
                          className={`font-display font-bold text-base ${
                            active ? "text-white" : "text-[#1A1A1A]"
                          }`}
                        >
                          {av.label}
                        </span>
                      </div>
                      <div
                        className={`text-xs mt-1.5 leading-relaxed ${
                          active ? "text-white/80" : "text-[#5C5C5C]"
                        }`}
                      >
                        {av.desc}
                      </div>
                    </button>
                  );
                })}
              </div>
            </fieldset>

            {/* Step 4 — Personal info */}
            <fieldset>
              <legend className="text-[10px] font-bold uppercase tracking-[0.22em] text-[#C15C3D] mb-5">
                4. I tuoi contatti
              </legend>
              <div className="rounded-3xl bg-white border border-[#E5E1D8] p-6 sm:p-8 grid md:grid-cols-2 gap-5">
                <div>
                  <label
                    htmlFor="vname"
                    className="block text-[10px] font-bold uppercase tracking-[0.18em] text-[#5C5C5C] mb-2"
                  >
                    Nome e cognome
                  </label>
                  <input
                    id="vname"
                    name="name"
                    type="text"
                    required
                    minLength={2}
                    maxLength={120}
                    value={form.name}
                    onChange={handleField}
                    data-testid="volunteer-input-name"
                    placeholder="Come ti chiami?"
                    className="w-full bg-[#FBF9F6] border border-[#E5E1D8] rounded-xl px-4 py-3 text-[#1A1A1A] placeholder:text-[#9A9A9A] focus:outline-none focus:border-[#2E4F3B] focus:ring-2 focus:ring-[#2E4F3B]/10 transition-all"
                  />
                </div>
                <div>
                  <label
                    htmlFor="vphone"
                    className="block text-[10px] font-bold uppercase tracking-[0.18em] text-[#5C5C5C] mb-2"
                  >
                    Telefono <span className="text-[#C15C3D] normal-case font-bold">*</span>
                  </label>
                  <input
                    id="vphone"
                    name="phone"
                    type="tel"
                    required
                    minLength={5}
                    maxLength={40}
                    value={form.phone}
                    onChange={handleField}
                    data-testid="volunteer-input-phone"
                    placeholder="Verrai aggiunto alla bacheca con questo numero"
                    className="w-full bg-[#FBF9F6] border border-[#E5E1D8] rounded-xl px-4 py-3 text-[#1A1A1A] placeholder:text-[#9A9A9A] focus:outline-none focus:border-[#2E4F3B] focus:ring-2 focus:ring-[#2E4F3B]/10 transition-all"
                  />
                </div>
                <div>
                  <label
                    htmlFor="vemail"
                    className="block text-[10px] font-bold uppercase tracking-[0.18em] text-[#5C5C5C] mb-2"
                  >
                    Email <span className="opacity-60 normal-case font-medium">(facolt.)</span>
                  </label>
                  <input
                    id="vemail"
                    name="email"
                    type="email"
                    value={form.email}
                    onChange={handleField}
                    data-testid="volunteer-input-email"
                    placeholder="tu@esempio.it"
                    className="w-full bg-[#FBF9F6] border border-[#E5E1D8] rounded-xl px-4 py-3 text-[#1A1A1A] placeholder:text-[#9A9A9A] focus:outline-none focus:border-[#2E4F3B] focus:ring-2 focus:ring-[#2E4F3B]/10 transition-all"
                  />
                </div>
                <div>
                  <label
                    htmlFor="vmessage"
                    className="block text-[10px] font-bold uppercase tracking-[0.18em] text-[#5C5C5C] mb-2"
                  >
                    Note <span className="opacity-60 normal-case font-medium">(facolt.)</span>
                  </label>
                  <input
                    id="vmessage"
                    name="message"
                    type="text"
                    maxLength={2000}
                    value={form.message}
                    onChange={handleField}
                    data-testid="volunteer-input-message"
                    placeholder="Esperienze, mezzi, fasce orarie…"
                    className="w-full bg-[#FBF9F6] border border-[#E5E1D8] rounded-xl px-4 py-3 text-[#1A1A1A] placeholder:text-[#9A9A9A] focus:outline-none focus:border-[#2E4F3B] focus:ring-2 focus:ring-[#2E4F3B]/10 transition-all"
                  />
                </div>
              </div>
            </fieldset>

            {/* Submit */}
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-4">
              <button
                type="submit"
                disabled={status === "loading"}
                data-testid="volunteer-submit-btn"
                className="inline-flex items-center justify-center gap-3 rounded-full bg-[#2E4F3B] text-white font-bold px-8 py-4 hover:bg-[#1f3828] transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {status === "loading" ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Invio in corso…
                  </>
                ) : (
                  <>
                    Mi candido come volontario
                    <Send className="w-4 h-4" />
                  </>
                )}
              </button>
              <p className="text-xs sm:text-sm text-[#5C5C5C] leading-relaxed max-w-sm">
                Ti aggiungeremo automaticamente alla bacheca dei volontari al
                numero che hai indicato.
              </p>
            </div>

            {status === "error" && (
              <div
                data-testid="volunteer-error"
                className="flex items-start gap-3 bg-[#C15C3D]/10 border border-[#C15C3D]/30 rounded-xl p-4 max-w-2xl"
              >
                <AlertCircle className="w-5 h-5 text-[#C15C3D] shrink-0 mt-0.5" />
                <div className="text-sm text-[#1A1A1A]">{error}</div>
              </div>
            )}
          </form>
        )}
      </div>
    </section>
  );
};
